import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, toast } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Combobox } from '../components/ui/combobox';
import { Trash2, Plus, Search, History } from 'lucide-react';

const WBSEstimations = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estimations, setEstimations] = useState([]);
  const [filteredEstimations, setFilteredEstimations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedWbsForHistory, setSelectedWbsForHistory] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  
  // Form data
  const [activeEstimations, setActiveEstimations] = useState([]);
  const [masterListItems, setMasterListItems] = useState([]);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const [wbsNumber, setWbsNumber] = useState('');
  const [type, setType] = useState('Structural');
  const [status, setStatus] = useState('Draft');
  const [currentVersion, setCurrentVersion] = useState('1.0');
  const [createNewVersion, setCreateNewVersion] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [additionalHours, setAdditionalHours] = useState({
    job_study: 0,
    rfi: 0,
    submittal: 0,
  });

  // Summary (calculated)
  const [summary, setSummary] = useState({
    modelling: { base_hours: 0, checking_hours: 0, total: 0 },
    detailing: { base_hours: 0, checking_hours: 0, total: 0 },
    erection: { base_hours: 0, checking_hours: 0, total: 0 },
    support: { base_hours: 0, checking_hours: 0, total: 0 },
    total_execution_hours: 0,
  });
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    fetchEstimations();
    fetchActiveEstimations();
  }, []);

  useEffect(() => {
    if (type) {
      fetchMasterListItems();
    }
  }, [type]);

  useEffect(() => {
    calculateSummary();
  }, [lineItems, additionalHours]);

  // Filter estimations based on search query and version toggle
  useEffect(() => {
    let filtered = estimations;
    
    // Filter by latest version if toggle is off
    if (!showAllVersions) {
      filtered = filtered.filter(est => est.is_latest_version !== false);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(est => 
        est.wbs_number?.toLowerCase().includes(query) ||
        est.estimation_number?.toLowerCase().includes(query)
      );
    }
    
    setFilteredEstimations(filtered);
  }, [searchQuery, estimations, showAllVersions]);

  const fetchEstimations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wbs/estimations');
      setEstimations(response.data);
    } catch (error) {
      console.error('Failed to fetch WBS estimations:', error);
      toast.error('Failed to load WBS estimations');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveEstimations = async () => {
    try {
      const response = await api.get('/estimations/active');
      setActiveEstimations(response.data);
    } catch (error) {
      console.error('Failed to fetch active estimations:', error);
    }
  };

  const fetchVersionHistory = async (wbsNumber) => {
    try {
      // Fetch all versions with the same wbs_number
      const response = await api.get('/wbs/estimations');
      const allVersions = response.data.filter(est => est.wbs_number === wbsNumber);
      // Sort by version descending (latest first)
      allVersions.sort((a, b) => parseFloat(b.version || '1.0') - parseFloat(a.version || '1.0'));
      setVersionHistory(allVersions);
      setVersionHistoryOpen(true);
    } catch (error) {
      console.error('Failed to fetch version history:', error);
      toast.error('Failed to load version history');
    }
  };

  const fetchMasterListItems = async () => {
    try {
      const response = await api.get(`/wbs/master-list?type=${type}`);
      setMasterListItems(response.data);
    } catch (error) {
      console.error('Failed to fetch master list items:', error);
    }
  };

  const handleCreate = async () => {
    if (activeEstimations.length === 0) {
      toast.error('No active estimation projects found');
      return;
    }

    setEditMode(false);
    setEditingId(null);
    setSelectedEstimation(null);
    setWbsNumber('');
    setType('Structural');
    setStatus('Draft');
    setCurrentVersion('1.0');
    setCreateNewVersion(false);
    setLineItems([]);
    setAdditionalHours({ job_study: 0, rfi: 0, submittal: 0 });
    setDialogOpen(true);
  };

  const handleEdit = async (estimation) => {
    setEditMode(true);
    setEditingId(estimation.id);
    setSelectedEstimation({
      id: estimation.estimation_id,
      estimation_number: estimation.estimation_number,
    });
    setWbsNumber(estimation.wbs_number);
    setType(estimation.type);
    setStatus(estimation.status || 'Draft');
    setCurrentVersion(estimation.version || '1.0');
    setCreateNewVersion(false);
    setLineItems(estimation.line_items);
    setAdditionalHours(estimation.additional_hours);
    setDialogOpen(true);
  };

  const handleEstimationChange = async (estimationId) => {
    const estimation = activeEstimations.find(e => e.id === estimationId);
    setSelectedEstimation(estimation);

    if (!editMode && estimationId) {
      try {
        const response = await api.get(`/wbs/estimations/next-number/${estimationId}`);
        setWbsNumber(response.data.wbs_number);
      } catch (error) {
        console.error('Failed to get WBS number:', error);
      }
    }
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: `temp_${Date.now()}`,
        item: '',
        complexity: 'Simple',
        work_types: [],
      },
    ]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const addWorkType = (lineIndex) => {
    const updated = [...lineItems];
    updated[lineIndex].work_types.push({
      work_type: 'Modelling',
      count: 1,
      hours_required: 0,
    });
    setLineItems(updated);
    calculateWorkTypeHours(lineIndex, updated[lineIndex].work_types.length - 1);
  };

  const removeWorkType = (lineIndex, workTypeIndex) => {
    const updated = [...lineItems];
    updated[lineIndex].work_types = updated[lineIndex].work_types.filter((_, i) => i !== workTypeIndex);
    setLineItems(updated);
  };

  const updateWorkType = (lineIndex, workTypeIndex, field, value) => {
    const updated = [...lineItems];
    updated[lineIndex].work_types[workTypeIndex][field] = value;
    setLineItems(updated);

    if (field === 'work_type' || field === 'count') {
      calculateWorkTypeHours(lineIndex, workTypeIndex);
    }
  };

  const calculateWorkTypeHours = async (lineIndex, workTypeIndex) => {
    const lineItem = lineItems[lineIndex];
    const workType = lineItem.work_types[workTypeIndex];

    if (!lineItem.item || !lineItem.complexity || !workType.work_type) {
      return;
    }

    // Find matching master list entry
    const masterEntry = masterListItems.find(
      m => m.item === lineItem.item && m.complexity === lineItem.complexity
    );

    if (!masterEntry) {
      return;
    }

    // Get hours in minutes from master list
    let minutesFromMaster = 0;
    switch (workType.work_type) {
      case 'Modelling':
        minutesFromMaster = masterEntry.hours_modelling;
        break;
      case 'Detailing':
        minutesFromMaster = masterEntry.hours_detailing;
        break;
      case 'Erection':
        minutesFromMaster = masterEntry.hours_erection;
        break;
      case 'Support':
        minutesFromMaster = masterEntry.hours_support;
        break;
    }

    // Calculate hours: (minutes * count) / 60
    const hours = (minutesFromMaster * (workType.count || 0)) / 60;

    // Update the hours_required
    const updated = [...lineItems];
    updated[lineIndex].work_types[workTypeIndex].hours_required = parseFloat(hours.toFixed(2));
    setLineItems(updated);
  };

  const calculateSummary = () => {
    const workTypeTotals = {
      Modelling: 0,
      Detailing: 0,
      Erection: 0,
      Support: 0,
    };

    // Sum base hours from all line items
    lineItems.forEach(lineItem => {
      lineItem.work_types.forEach(wt => {
        if (workTypeTotals.hasOwnProperty(wt.work_type)) {
          workTypeTotals[wt.work_type] += wt.hours_required || 0;
        }
      });
    });

    // Calculate summary with 30% checking
    const newSummary = {
      modelling: {
        base_hours: parseFloat(workTypeTotals.Modelling.toFixed(2)),
        checking_hours: parseFloat((workTypeTotals.Modelling * 0.3).toFixed(2)),
        total: parseFloat((workTypeTotals.Modelling * 1.3).toFixed(2)),
      },
      detailing: {
        base_hours: parseFloat(workTypeTotals.Detailing.toFixed(2)),
        checking_hours: parseFloat((workTypeTotals.Detailing * 0.3).toFixed(2)),
        total: parseFloat((workTypeTotals.Detailing * 1.3).toFixed(2)),
      },
      erection: {
        base_hours: parseFloat(workTypeTotals.Erection.toFixed(2)),
        checking_hours: parseFloat((workTypeTotals.Erection * 0.3).toFixed(2)),
        total: parseFloat((workTypeTotals.Erection * 1.3).toFixed(2)),
      },
      support: {
        base_hours: parseFloat(workTypeTotals.Support.toFixed(2)),
        checking_hours: parseFloat((workTypeTotals.Support * 0.3).toFixed(2)),
        total: parseFloat((workTypeTotals.Support * 1.3).toFixed(2)),
      },
    };

    newSummary.total_execution_hours = parseFloat(
      (newSummary.modelling.total + newSummary.detailing.total + newSummary.erection.total + newSummary.support.total).toFixed(2)
    );

    setSummary(newSummary);

    // Calculate grand total (rounded up)
    const totalSupport = (additionalHours.job_study || 0) + (additionalHours.rfi || 0) + (additionalHours.submittal || 0);
    const grand = Math.ceil(newSummary.total_execution_hours + totalSupport);
    setGrandTotal(grand);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEstimation) {
      toast.error('Please select an estimation project');
      return;
    }

    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    const payload = {
      estimation_id: selectedEstimation.id,
      estimation_number: selectedEstimation.estimation_number,
      type,
      line_items: lineItems,
      additional_hours: {
        job_study: parseFloat(additionalHours.job_study) || 0,
        rfi: parseFloat(additionalHours.rfi) || 0,
        submittal: parseFloat(additionalHours.submittal) || 0,
        total_support_hours: 0,
      },
      status,
      create_new_version: createNewVersion,
    };

    try {
      if (editMode) {
        await api.put(`/wbs/estimations/${editingId}`, payload);
        if (createNewVersion) {
          toast.success('New version created successfully');
        } else {
          toast.success('WBS estimation updated successfully');
        }
      } else {
        await api.post('/wbs/estimations', payload);
        toast.success('WBS estimation created successfully');
      }

      setDialogOpen(false);
      fetchEstimations();
    } catch (error) {
      console.error('Failed to save WBS estimation:', error);
      toast.error(error.response?.data?.detail || 'Failed to save WBS estimation');
    }
  };

  const handleDelete = async (id, wbsNumber) => {
    if (!window.confirm(`Are you sure you want to delete WBS ${wbsNumber}?`)) {
      return;
    }

    try {
      await api.delete(`/wbs/estimations/${id}`);
      toast.success('WBS estimation deleted successfully');
      fetchEstimations();
    } catch (error) {
      console.error('Failed to delete WBS estimation:', error);
      toast.error('Failed to delete WBS estimation');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-md3-display-small">WBS Estimations</h1>
            <p className="text-md3-on-surface-variant mt-1">Manage Work Breakdown Structure estimations</p>
          </div>
          <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleCreate}>
            Create WBS
          </Button>
        </div>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <CardTitle className="text-md3-title-large text-md3-on-surface ">All WBS Estimations</CardTitle>
                <label className="flex items-center gap-2 text-md3-body-small cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllVersions}
                    onChange={(e) => setShowAllVersions(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-md3-on-surface-variant">Show all versions</span>
                </label>
              </div>
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search by Estimation # or Project..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md3-md border">
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>WBS Number</TableHead>
                    <TableHead>Estimation Project</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead className="text-right">Grand Total (hrs)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEstimations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-md3-on-surface-variant py-8">
                        {searchQuery ? 'No WBS estimations match your search.' : 'No WBS estimations found. Click "Create WBS" to add one.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEstimations.map((estimation) => (
                      <TableRow key={estimation.id}>
                        <TableCell className="font-medium">{estimation.wbs_number}</TableCell>
                        <TableCell>{estimation.estimation_number}</TableCell>
                        <TableCell>{estimation.type}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            v{estimation.version || '1.0'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{estimation.grand_total_hours}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            estimation.status === 'Approved' ? 'bg-green-100 text-green-800' : 
                            estimation.status === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {estimation.status}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(estimation.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              size="sm"
                              onClick={() => fetchVersionHistory(estimation.wbs_number)}
                              title="View Version History"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                               onClick={() => handleEdit(estimation)}>
                              Edit
                            </Button>
                            <Button variant="destructive"
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                              onClick={() => handleDelete(estimation.id, estimation.wbs_number)}>
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                {editMode ? `Edit WBS: ${wbsNumber}` : 'Create WBS Estimation'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit}>
              {/* Header Section */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="space-y-2">
                  <Label>Estimation Project *</Label>
                  <Combobox
                    options={activeEstimations.map((est) => ({
                      value: est.id,
                      label: est.display_name || `${est.estimation_number} - ${est.project_name || 'Unnamed Project'}`
                    }))}
                    value={selectedEstimation?.id || ''}
                    onChange={handleEstimationChange}
                    placeholder="Select estimation"
                    searchPlaceholder="Search estimations..."
                    emptyMessage="No active estimations found."
                    disabled={editMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Structural">Structural</SelectItem>
                      <SelectItem value="Miscellaneous">Miscellaneous</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {wbsNumber && (
                  <div className="space-y-2">
                    <Label>WBS Number</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container" value={wbsNumber} disabled  />
                  </div>
                )}
              </div>

              {/* Status and Version Section */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-md3-surface rounded-md3-lg border">
                <div className="space-y-2">
                  <Label>Status *</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Submitted">Submitted</SelectItem>
                      <SelectItem value="Approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editMode && (
                  <div className="space-y-2">
                    <Label>Current Version</Label>
                    <div className="flex items-center space-x-2">
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container w-24" value={`v${currentVersion}`} 
                        disabled 
                         
                      />
                      <span className="text-md3-body-small text-md3-on-surface-variant">
                        {createNewVersion ? `→ v${(parseFloat(currentVersion) + 0.1).toFixed(1)}` : '(updating)'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Block 1: Line Items */}
              <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Block 1: Line Items</CardTitle>
                    <Button type="button" size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={addLineItem}>
                      <Plus className="h-4 w-4 mr-1" /> Add Line
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {lineItems.length === 0 ? (
                    <p className="text-center text-md3-on-surface-variant py-4">No line items. Click &quot;Add Line&quot; to start.</p>
                  ) : (
                    <div className="space-y-6">
                      {lineItems.map((lineItem, lineIndex) => (
                        <div key={lineItem.id} className="border rounded-md3-lg p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="grid grid-cols-3 gap-3 flex-1">
                              <div className="space-y-2">
                                <Label>Item</Label>
                                <Select
                                  value={lineItem.item}
                                  onValueChange={(value) => updateLineItem(lineIndex, 'item', value)}
                                >
                                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                                    <SelectValue placeholder="Select item" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from(new Set(masterListItems.map(m => m.item))).map((item) => (
                                      <SelectItem key={item} value={item}>{item}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label>Complexity</Label>
                                <Select
                                  value={lineItem.complexity}
                                  onValueChange={(value) => updateLineItem(lineIndex, 'complexity', value)}
                                >
                                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Simple">Simple</SelectItem>
                                    <SelectItem value="Medium">Medium</SelectItem>
                                    <SelectItem value="Complex">Complex</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="flex items-end">
                                <Button type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                  onClick={() => removeLineItem(lineIndex)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Work Types */}
                          <div className="pl-4 border-l-2 space-y-2">
                            <div className="flex justify-between items-center">
                              <Label className="text-md3-body-small font-medium ">Work Types</Label>
                              <Button type="button"
                                variant="outline"
                                size="sm"
                                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => addWorkType(lineIndex)}>
                                <Plus className="h-3 w-3 mr-1" /> Add Work Type
                              </Button>
                            </div>

                            {lineItem.work_types.map((workType, wtIndex) => (
                              <div key={wtIndex} className="grid grid-cols-5 gap-2 items-end bg-md3-surface p-2 rounded">
                                <div className="space-y-1">
                                  <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Work Type</Label>
                                  <Select
                                    value={workType.work_type}
                                    onValueChange={(value) => updateWorkType(lineIndex, wtIndex, 'work_type', value)}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Modelling">Modelling</SelectItem>
                                      <SelectItem value="Detailing">Detailing</SelectItem>
                                      <SelectItem value="Erection">Erection</SelectItem>
                                      <SelectItem value="Support">Support</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1">
                                  <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Count</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 h-8 no-spinner" type="number"
                                    min="0"
                                    step="0.1"
                                    
                                    placeholder=""
                                    value={workType.count || ''}
                                    onChange={(e) => updateWorkType(lineIndex, wtIndex, 'count', parseFloat(e.target.value) || 0)}
                                  />
                                </div>

                                <div className="space-y-1 col-span-2">
                                  <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Hours Required</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 h-8 bg-md3-surface-container" type="number"
                                    
                                    value={workType.hours_required}
                                    disabled
                                  />
                                </div>

                                <Button type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"  onClick={() => removeWorkType(lineIndex, wtIndex)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Block 2: Summary */}
              <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
                <CardHeader>
                  <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Block 2: Summary (Auto-Calculated)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Work Type</TableHead>
                        <TableHead className="text-right">Base Hours</TableHead>
                        <TableHead className="text-right">Checking (30%)</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Modelling</TableCell>
                        <TableCell className="text-right">{summary.modelling.base_hours}</TableCell>
                        <TableCell className="text-right">{summary.modelling.checking_hours}</TableCell>
                        <TableCell className="text-right font-semibold">{summary.modelling.total}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Detailing</TableCell>
                        <TableCell className="text-right">{summary.detailing.base_hours}</TableCell>
                        <TableCell className="text-right">{summary.detailing.checking_hours}</TableCell>
                        <TableCell className="text-right font-semibold">{summary.detailing.total}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Erection</TableCell>
                        <TableCell className="text-right">{summary.erection.base_hours}</TableCell>
                        <TableCell className="text-right">{summary.erection.checking_hours}</TableCell>
                        <TableCell className="text-right font-semibold">{summary.erection.total}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Support</TableCell>
                        <TableCell className="text-right">{summary.support.base_hours}</TableCell>
                        <TableCell className="text-right">{summary.support.checking_hours}</TableCell>
                        <TableCell className="text-right font-semibold">{summary.support.total}</TableCell>
                      </TableRow>
                      <TableRow className="bg-md3-surface font-bold">
                        <TableCell colSpan={3}>Total Execution Hours</TableCell>
                        <TableCell className="text-right text-lg">{summary.total_execution_hours}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Block 3: Additional Hours */}
              <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
                <CardHeader>
                  <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Block 3: Additional Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>Job Study (hours)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" type="number"
                        min="0"
                        step="0.1"
                        
                        placeholder=""
                        value={additionalHours.job_study || ''}
                        onChange={(e) => setAdditionalHours({ ...additionalHours, job_study: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>RFI (hours)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" type="number"
                        min="0"
                        step="0.1"
                        
                        placeholder=""
                        value={additionalHours.rfi || ''}
                        onChange={(e) => setAdditionalHours({ ...additionalHours, rfi: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Submittal (hours)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" type="number"
                        min="0"
                        step="0.1"
                        
                        placeholder=""
                        value={additionalHours.submittal || ''}
                        onChange={(e) => setAdditionalHours({ ...additionalHours, submittal: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-md3-body-small">
                      <span className="font-semibold">Total Support Hours:</span>{' '}
                      {((additionalHours.job_study || 0) + (additionalHours.rfi || 0) + (additionalHours.submittal || 0)).toFixed(2)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Grand Total */}
              <Card className="mb-6 bg-green-50 border-green-200 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-md3-title-large">GRAND TOTAL HOURS</h3>
                    <div className="text-md3-display-small text-green-700">{grandTotal} hours</div>
                  </div>
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">(Rounded up from {(summary.total_execution_hours + ((additionalHours.job_study || 0) + (additionalHours.rfi || 0) + (additionalHours.submittal || 0))).toFixed(2)})</p>
                </CardContent>
              </Card>

              <DialogFooter className="flex justify-between items-center">
                <Button type="button" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                
                {editMode ? (
                  <div className="flex gap-2">
                    <Button type="submit" 
                      variant="outline"
                      onClick={() => setCreateNewVersion(false)}
                      className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                    >
                      Update Current Version
                    </Button>
                    <Button type="submit"
                      onClick={() => setCreateNewVersion(true)}
                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                    >
                      Save as New Version
                    </Button>
                  </div>
                ) : (
                  <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                    Create WBS
                  </Button>
                )}
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Version History Dialog */}
        <Dialog open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen}>
          <DialogContent className="max-w-4xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Version History - {versionHistory[0]?.wbs_number}</DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Grand Total (hrs)</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Updated By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versionHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-md3-on-surface-variant">
                        No version history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    versionHistory.map((version, index) => (
                      <TableRow key={version.id} className={`${["bg-blue-50/50", "bg-green-50/50", "bg-purple-50/50", "bg-amber-50/50", "bg-pink-50/50", "bg-cyan-50/50"][index % 6]} hover:bg-opacity-70 transition-colors`}>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            version.is_latest_version !== false 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-md3-surface-container text-md3-on-surface'
                          }`}>
                            v{version.version || '1.0'}
                            {version.is_latest_version !== false && ' (Latest)'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            version.status === 'Approved' ? 'bg-green-100 text-green-800' : 
                            version.status === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {version.status}
                          </span>
                        </TableCell>
                        <TableCell>{version.type}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {version.grand_total_hours}
                        </TableCell>
                        <TableCell>
                          {new Date(version.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                          {version.updated_by || version.created_by || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                            size="sm"
                            onClick={() => {
                              handleEdit(version);
                              setVersionHistoryOpen(false);
                            }}
                          >
                            View/Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setVersionHistoryOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WBSEstimations;
